import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { SearchQueryProps } from '@/components/app/cards/ContentCard/types';
import {
	isProgramOrGuide,
	isRenderablePackItem,
	Pack,
	PackCardItem,
	RenderablePackItem,
} from '@/components/app/cards/PacksList/types';
import FaveHeart from '@/components/app/contentPlayer/FaveHeart';
import NarratorRow from '@/components/app/ui/NarratorRow';
import PackClassLink from '@/components/app/ui/PackClassLink';
import Image from '@/components/image/NextImage';
import { usePackItemAttributes } from '@/hooks/app/usePackItemAttributes';
import { formatResizedUrl } from '@/utils/app/formatResizedUrl';

import messages from './messages';
import {
	CoverArtWrapper,
	Duration,
	FaveHeartContainer,
	InfoContainer,
	LockIcon,
	PlayButton,
	PlayIcon,
	PlaylistControls,
	PlaylistHeaderWrapper,
	PlaylistInfo,
	PlaylistTitle,
	RowContainer,
	RowSubtitle,
	RowTitle,
	RowWrapper,
	ShuffleButton,
	ShuffleIcon,
	Subtitle,
	ThumbnailWrapper,
	Title,
	TitleContainer,
	TrackSummaryText,
	Wrapper,
} from './styles';
import { PackRowListProps } from './types';

// The thumbnail is a fixed small square, unlike ContentCard's tile which scales with viewport —
// requesting one fixed size avoids re-requesting a resized asset per breakpoint.
const ROW_THUMBNAIL_SIZE = 128;

function createSubtitle(field1?: string, field2?: string) {
	if (field1 && field2) {
		return `${field1} • ${field2}`;
	}

	return field1 ?? field2;
}

interface PackRowProps {
	pack: Pack;
	packItem: RenderablePackItem;
	packFeedName?: string;
	searchQuery?: SearchQueryProps;
}

const PackRow = ({ pack, packItem, packFeedName, searchQuery }: PackRowProps) => {
	const { formatMessage } = useIntl();
	const {
		title,
		subtitle,
		duration,
		image,
		content_type,
		narrator_name,
		promotional_text,
		full_subtitle,
		is_unlocked,
		guide_id,
		handlePackItemClick,
	} = usePackItemAttributes({
		pack,
		packItem: packItem as PackCardItem,
		packFeedName,
		searchQuery,
	});

	// promotional_text sometimes carries the real episode-specific title (e.g. Daily Move rows,
	// where `title` is just the generic program name repeated on every item) and sometimes just
	// repeats narrator_name as a credit — only the former is worth promoting over `title`. The
	// narrator_name check also scopes this to guide/program items (the only ones that carry it),
	// leaving non-narrated pack items (e.g. a nested pack link) on their own title unconditionally.
	const hasDistinctPromotionalTitle =
		Boolean(promotional_text) && Boolean(narrator_name) && promotional_text !== narrator_name;
	const titleText = hasDistinctPromotionalTitle ? promotional_text : title;
	// full_subtitle carries a punchy one-line description (e.g. "Mel helps turn your bad day
	// into a good night's sleep.") distinct from the date-and-title-echoing `subtitle` field —
	// prefer it once titleText has already absorbed promotional_text, so the row doesn't show
	// the same phrase twice.
	const subtitleText = hasDistinctPromotionalTitle
		? (full_subtitle ?? subtitle ?? createSubtitle(narrator_name, content_type))
		: (subtitle ?? createSubtitle(narrator_name, content_type) ?? promotional_text);

	return (
		<RowWrapper>
			<RowContainer
				onClick={() => {
					handlePackItemClick().catch(() => {});
				}} // Don't pass e to the above click event
				onKeyDown={e => {
					if (e.nativeEvent instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') {
						return;
					}
					// Space's default action is page scroll; this row has no href for it to fall
					// back to, so without this the row activates AND the page scrolls under it.
					e.preventDefault();
					handlePackItemClick().catch(() => {});
				}}
				tabIndex={0}
				role="button"
				aria-label={formatMessage(messages.label, { title: titleText })}
			>
				<ThumbnailWrapper>
					{/* An item the client has no render branch for reaches here with no usable
					    asset. formatResizedUrl calls .replace on its first argument, and next/image
					    rejects an empty src, so both would throw during render — render the row
					    without artwork instead of taking the surface down. */}
					{image?.url && (
						<Image
							fill
							style={{ objectFit: 'cover' }}
							src={formatResizedUrl(image.url, ROW_THUMBNAIL_SIZE, ROW_THUMBNAIL_SIZE)}
							alt=""
							sizes="64px"
						/>
					)}
					{!is_unlocked && <LockIcon data-testid="lock-icon" />}
				</ThumbnailWrapper>
				<InfoContainer>
					{titleText && <RowTitle>{titleText}</RowTitle>}
					{subtitleText && <RowSubtitle>{subtitleText}</RowSubtitle>}
					{duration && <Duration>{duration}</Duration>}
				</InfoContainer>
			</RowContainer>
			{/* A sibling of the clickable row, not nested inside it — matching GuideRow's own
			    split, so a heart click doesn't also fire the row's own onClick. Only guide items
			    carry a guide_id faves can key on; other pack item types show no heart. */}
			{guide_id && (
				<FaveHeartContainer>
					<FaveHeart guideId={guide_id} guideTitle={titleText ?? ''} />
				</FaveHeartContainer>
			)}
		</RowWrapper>
	);
};

function isItemUnlocked(packItem: RenderablePackItem): boolean {
	return packItem.is_unlocked !== false;
}

// The API sends duration in seconds per item and no pre-aggregated total, so the playlist
// header's "H hr M min" summary has to be computed client-side from the raw item list —
// not `supportedItems`, since the total should reflect the whole pack, including any item
// types this client doesn't render as a row.
function sumDurationSeconds(items: Pack['items']): number {
	return items.reduce((total, item) => {
		const duration = 'duration' in item && typeof item.duration === 'number' ? item.duration : 0;
		return total + duration;
	}, 0);
}

function formatTotalDuration(totalSeconds: number): string {
	const totalMinutes = Math.round(totalSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	// "0 hr 45 min" reads as broken to anyone under an hour in — drop the hours segment
	// entirely rather than showing a zero.
	return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
}

interface PackPlaybackProxyProps {
	pack: Pack;
	packItem: RenderablePackItem;
	packFeedName?: string;
	onTriggered: () => void;
}

// Renders nothing — exists only to call usePackItemAttributes for a single chosen item and
// fire its handlePackItemClick once mounted. This reuses the exact same locked-content/upsell
// routing and unlocked-guide-play routing every row already goes through on a real click,
// instead of a second hand-rolled path for the header's Play/Shuffle buttons.
const PackPlaybackProxy = ({ pack, packItem, packFeedName, onTriggered }: PackPlaybackProxyProps) => {
	const { handlePackItemClick } = usePackItemAttributes({
		pack,
		packItem: packItem as PackCardItem,
		packFeedName,
	});

	useEffect(() => {
		// The parent only mounts this proxy at the moment a target item is chosen, and
		// unmounts it via onTriggered once the click resolves — so this is deliberately a
		// fire-once-per-mount effect, not one that should re-run if handlePackItemClick's
		// identity changes for unrelated reasons.
		handlePackItemClick()
			.catch(() => {})
			.finally(onTriggered);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

interface PlaylistHeaderProps {
	pack: Pack;
	supportedItems: RenderablePackItem[];
	packFeedName?: string;
}

const PLAYLIST_COVER_ART_SIZE = 320;

const PlaylistHeader = ({ pack, supportedItems, packFeedName }: PlaylistHeaderProps) => {
	const { formatMessage } = useIntl();
	// Holds the single item a Play/Shuffle click just chose, so exactly one PackPlaybackProxy
	// mounts, fires its click once, then unmounts (see onTriggered) — ready for the next click,
	// even a repeat pick of the same item.
	const [clickTarget, setClickTarget] = useState<RenderablePackItem | null>(null);

	const unlockedItems = supportedItems.filter(isItemUnlocked);
	// supportedItems is guaranteed non-empty here — PackRowList returns null before rendering
	// this header when there are zero renderable items.
	const fallbackItem = supportedItems[0];
	const curator = pack.associations?.[0];
	const trackCount = pack.total_item_number ?? supportedItems.length;
	const durationText = formatTotalDuration(sumDurationSeconds(pack.items));
	const title = pack.title ?? '';

	return (
		<PlaylistHeaderWrapper>
			<CoverArtWrapper>
				{pack.image?.url && (
					<Image
						fill
						style={{ objectFit: 'cover' }}
						src={formatResizedUrl(pack.image.url, PLAYLIST_COVER_ART_SIZE, PLAYLIST_COVER_ART_SIZE)}
						alt=""
						sizes="160px"
					/>
				)}
			</CoverArtWrapper>
			<PlaylistInfo>
				{pack.title && <PlaylistTitle>{pack.title}</PlaylistTitle>}
				{curator && (
					<NarratorRow
						image={curator.image}
						title={curator.title}
						href={curator.narrator_id ? `/app/narrator-${curator.narrator_id}` : undefined}
						ariaLabel={formatMessage(messages.curatorAriaLabel, { curator: curator.title })}
					/>
				)}
				<PlaylistControls>
					<PlayButton
						type="button"
						aria-label={formatMessage(messages.playAriaLabel, { title })}
						onClick={() => setClickTarget(unlockedItems[0] ?? fallbackItem)}
					>
						<PlayIcon />
						{formatMessage(messages.playLabel)}
					</PlayButton>
					<ShuffleButton
						type="button"
						aria-label={formatMessage(messages.shuffleAriaLabel, { title })}
						onClick={() => {
							const randomUnlocked =
								unlockedItems.length > 0
									? unlockedItems[Math.floor(Math.random() * unlockedItems.length)]
									: undefined;
							setClickTarget(randomUnlocked ?? fallbackItem);
						}}
					>
						<ShuffleIcon>
							<polyline points="16 3 21 3 21 8" />
							<line x1="4" y1="20" x2="21" y2="3" />
							<polyline points="21 16 21 21 16 21" />
							<line x1="15" y1="15" x2="21" y2="21" />
							<line x1="4" y1="4" x2="9" y2="9" />
						</ShuffleIcon>
						{formatMessage(messages.shuffleLabel)}
					</ShuffleButton>
				</PlaylistControls>
				<TrackSummaryText>
					{formatMessage(messages.trackSummary, { count: trackCount, duration: durationText })}
				</TrackSummaryText>
			</PlaylistInfo>
			{clickTarget && (
				<PackPlaybackProxy
					pack={pack}
					packItem={clickTarget}
					packFeedName={packFeedName}
					onTriggered={() => setClickTarget(null)}
				/>
			)}
		</PlaylistHeaderWrapper>
	);
};

const PackRowList = ({ pack, packFeedName, searchQuery }: PackRowListProps) => {
	// Keep each item's position in the server's original (pre-filter) array so
	// pack_item_index reflects the server's ranking, matching how mobile stamps
	// position before any client-side filtering — not this component's own
	// render-order index into the type-filtered subset.
	const supportedItems = pack.items
		.map((packItem, serverIndex) => ({ packItem, serverIndex }))
		.filter((entry): entry is { packItem: RenderablePackItem; serverIndex: number } =>
			isRenderablePackItem(entry.packItem),
		);

	// A title/subtitle header over zero rows reads as a broken page, not an empty one —
	// hide the whole section rather than orphan the header above nothing.
	if (supportedItems.length === 0) {
		return null;
	}

	return (
		<>
			{pack.pack_type === 'playlist' ? (
				<PlaylistHeader
					pack={pack}
					supportedItems={supportedItems.map(({ packItem }) => packItem)}
					packFeedName={packFeedName}
				/>
			) : (
				<TitleContainer>
					{pack.subtitle && <Subtitle>{pack.subtitle}</Subtitle>}
					{pack.title && !pack.subtitle && <Title>{pack.title}</Title>}
					{pack.has_more && <PackClassLink pack={pack} />}
				</TitleContainer>
			)}
			<Wrapper>
				{supportedItems.map(({ packItem, serverIndex }) => (
					<PackRow
						pack={pack}
						// `pack`-type items carry neither guide_id nor program_id, so they always
						// fall through to the positional fallback below.
						key={
							(isProgramOrGuide(packItem) ? (packItem.guide_id ?? packItem.program_id) : undefined) ??
							`${pack.id}-${serverIndex}`
						}
						packItem={packItem}
						packFeedName={packFeedName}
						searchQuery={{
							...searchQuery,
							pack_item_index: serverIndex,
						}}
					/>
				))}
			</Wrapper>
		</>
	);
};

export default PackRowList;
